import * as React from 'react';

import { ScaleRecord } from '@rematter/modules/settings/types/sidecar';
import {
  Badge,
  Button,
  Col,
  Row,
  SearchField,
  ThemeColor,
  TinyTable,
} from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import {
  LucideCpu,
  LucidePlus,
  LucideRadioTower,
  LucideWeight,
  LucideWrench,
} from 'lucide-react';

import { useScaleEventsSubscription } from '../../../_graphql/subscriptionScaleEvents';
import * as state from '../../../_state/scalesList.state';
import { useSettingsLocation } from '../../../_state/settingsModule.state';
import { useTranslations } from '../../../i18n/useTranslations';

import { DemoScaleCreateDialog } from './DemoScaleCreateDialog';
import { ScaleCreateDialog } from './ScaleCreateDialog';

import { useLibp2p } from '~/shared/providers/libp2p';
import { useHardwarePresenceMembers } from '~/shared/providers/presence';

const STATUS_COLORS: Record<string, ThemeColor> = {
  Discovered: 'neutral',
  Configured: 'warning',
  Active: 'success',
};

const ScaleStatusCell: React.FC<{ scale: ScaleRecord }> = ({ scale }) => {
  const t = useTranslations();
  const { connections } = useLibp2p();
  const presenceMembers = useHardwarePresenceMembers();
  const isOnline = presenceMembers.has(scale.id);
  const conn = connections.get(scale.sidecarId);

  return (
    <Row gap1 className="@ScaleStatusCell items-center">
      <Badge color={STATUS_COLORS[scale.status]}>
        {t.hardwareView.scalesPanel.statusLabels[scale.status]}
      </Badge>
      <Badge color={isOnline ? 'success' : 'neutral'}>
        {isOnline
          ? t.hardwareView.scalesPanel.presenceLabels.online
          : t.hardwareView.scalesPanel.presenceLabels.offline}
      </Badge>
      {conn?.connected && (
        <Badge color={conn.type === 'direct' ? 'primary' : 'neutral'}>
          {conn.type === 'direct'
            ? t.hardwareView.scalesPanel.connectionLabels.direct
            : t.hardwareView.scalesPanel.connectionLabels.relayed}
        </Badge>
      )}
    </Row>
  );
};

export const ScalesListView = () => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();
  const [searchTerm, setSearchTerm] = React.useState('');
  const [showCreateDialog, setShowCreateDialog] = React.useState(false);
  const [showDemoDialog, setShowDemoDialog] = React.useState(false);
  const { data, isFetching, loadingState, error, refetch } = useAtomValue(
    state.$remoteScales,
  );

  useScaleEventsSubscription(undefined, {
    onEvent: () => {
      refetch();
    },
  });

  const allScales = data?.scales ?? [];

  const scales = React.useMemo(() => {
    if (!searchTerm.trim()) return allScales;
    const term = searchTerm.toLowerCase();
    return allScales.filter(
      (c) =>
        c.name.toLowerCase().includes(term) ||
        c.scaleFormatType.toLowerCase().includes(term) ||
        c.connectionType.toLowerCase().includes(term) ||
        c.sidecar?.name.toLowerCase().includes(term),
    );
  }, [allScales, searchTerm]);

  return (
    <Col gap4 className="@ScalesListView h-full p-4">
      <Row gap2>
        <SearchField
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder={t.hardwareView.scalesPanel.search.placeholder}
          aria-label={t.hardwareView.scalesPanel.search.ariaLabel}
          className="w-60 shrink-0"
        />
        <Button
          className="ml-auto"
          icon={<LucidePlus className="size-4" />}
          onPress={() => setShowDemoDialog(true)}
        >
          {t.hardwareView.demoCreateDialog.actions.addDemoScale}
        </Button>
        <Button
          icon={<LucidePlus className="size-4" />}
          onPress={() => setShowCreateDialog(true)}
        >
          {t.hardwareView.scalesPanel.actions.addScale}
        </Button>
      </Row>
      <TinyTable<ScaleRecord>
        rowKey={(row) => row.id}
        isLoading={loadingState.isInitialLoading || isFetching}
        error={error}
        onRowClick={(row) =>
          setLocation(
            `/hardware/scales/${encodeURIComponent(row.sidecarId)}/${encodeURIComponent(row.id)}`,
          )
        }
        columns={[
          {
            key: 'name',
            label: t.hardwareView.scalesPanel.columns.name,
            icon: LucideWeight,
            width: 'auto',
            cell: (row) => (
              <div className="truncate font-medium">{row.name}</div>
            ),
          },
          {
            key: 'scaleFormatType',
            label: t.hardwareView.scalesPanel.columns.formatType,
            icon: LucideWrench,
            width: 180,
            cell: (row) => (
              <div className="truncate">{row.scaleFormatType}</div>
            ),
          },
          {
            key: 'connectionType',
            label: t.hardwareView.scalesPanel.columns.connectionType,
            icon: LucideWrench,
            width: 140,
            cell: (row) => <div className="truncate">{row.connectionType}</div>,
          },
          {
            key: 'status',
            label: t.hardwareView.scalesPanel.columns.status,
            icon: LucideRadioTower,
            width: 'max-content',
            cell: (row) => <ScaleStatusCell scale={row} />,
          },
          {
            key: 'sidecar',
            label: t.hardwareView.scalesPanel.columns.sidecar,
            icon: LucideCpu,
            width: 220,
            cell: (row) => (
              <div className="truncate">{row.sidecar?.name ?? '\u2013'}</div>
            ),
          },
        ]}
        data={scales}
      />
      <ScaleCreateDialog
        isOpen={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onSuccess={() => refetch()}
      />
      <DemoScaleCreateDialog
        isOpen={showDemoDialog}
        onClose={() => setShowDemoDialog(false)}
        onSuccess={() => refetch()}
      />
    </Col>
  );
};
